import React, { useState, useEffect, useRef } from 'react';
import { Camera, Package, BarChart3, Calendar, Plus, X, Edit2, Trash2, Check } from 'lucide-react';
import { extractAddressFromImage } from './ocrAddress';

const DHLPackageTracker = () => {
  const [view, setView] = useState('dashboard');
  const [packages, setPackages] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [formData, setFormData] = useState({
    address: '',
    weight: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [editingId, setEditingId] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('dhl-packages');
    if (stored) {
      setPackages(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('dhl-packages', JSON.stringify(packages));
  }, [packages]);

	const startCamera = async () => {
	  try {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
		  alert('Kamera wird von diesem Browser nicht unterstützt.');
		  return;
		}

		const stream = await navigator.mediaDevices.getUserMedia({
		  video: {
			width: { ideal: 1920 },
			height: { ideal: 1080 },
			facingMode: { ideal: 'environment' } // Rückkamera, falls verfügbar
		  },
		  audio: false
		});

		if (videoRef.current) {
		  videoRef.current.srcObject = stream;

		  // Warten bis Metadaten geladen sind -> dann erst play()
		  videoRef.current.onloadedmetadata = () => {
			videoRef.current.play().catch(err => {
			  console.error('Video play() Fehler:', err);
			});
		  };
		}

		setShowCamera(true);
	  } catch (err) {
		console.error('Kamera Fehler:', err);
		alert('Kamera konnte nicht gestartet werden. Bitte Berechtigungen prüfen.');
	  }
	};


	const capturePhoto = () => {
	  const video = videoRef.current;
	  const canvas = canvasRef.current;

	  if (!video || !canvas) {
		alert('Kamera ist nicht bereit.');
		return;
	  }

	  // readyState 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA
	  if (video.readyState < 2) {
		alert('Kamera lädt noch, bitte einen Moment warten und dann erneut aufnehmen.');
		return;
	  }

	  const width = video.videoWidth || 1280;
	  const height = video.videoHeight || 720;

	  if (!width || !height) {
		alert('Kein gültiges Videobild erhalten.');
		return;
	  }

	  canvas.width = width;
	  canvas.height = height;

	  const ctx = canvas.getContext('2d');
	  ctx.drawImage(video, 0, 0, width, height);

	  const imageData = canvas.toDataURL('image/jpeg', 0.7);

	  setCapturedImage(imageData);
	  stopCamera();
	  extractAddress(imageData);
	};


  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = canvasRef.current;
          const maxWidth = 1200;
          const scale = Math.min(1, maxWidth / img.width);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          const imageData = canvas.toDataURL('image/jpeg', 0.7);
          setCapturedImage(imageData);
          extractAddress(imageData);
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

	const extractAddress = async (imageData) => {
	  try {
		// Feedback für den Nutzer, dass etwas passiert
		setFormData(prev => ({ ...prev, address: 'Adresse wird erkannt …' }));

		const { rawText, address } = await extractAddressFromImage(imageData);

		if (address) {
		  setFormData(prev => ({ ...prev, address }));
		} else {
		  console.log('OCR-Text ohne erkannte Adresse:', rawText);
		  setFormData(prev => ({ ...prev, address: 'Manuelle Eingabe erforderlich' }));
		}
	  } catch (err) {
		console.error('Fehler bei der Adressextraktion (OCR):', err);
		setFormData(prev => ({ ...prev, address: 'Manuelle Eingabe erforderlich' }));
	  }
	};

  const savePackage = () => {
    if (!formData.address || !formData.weight || !formData.date) {
      alert('Bitte alle Felder ausfüllen');
      return;
    }

    const packageData = {
      id: editingId || Date.now(),
      ...formData,
      image: capturedImage,
      weight: parseFloat(formData.weight)
    };

    if (editingId) {
      setPackages(packages.map(p => p.id === editingId ? packageData : p));
      setEditingId(null);
    } else {
      setPackages([...packages, packageData]);
    }

    resetForm();
    setView('dashboard');
  };

  const resetForm = () => {
    setFormData({
      address: '',
      weight: '',
      date: new Date().toISOString().split('T')[0]
    });
    setCapturedImage(null);
    setEditingId(null);
  };

  const deletePackage = (id) => {
    if (window.confirm('Paket wirklich löschen?')) {
      setPackages(packages.filter(p => p.id !== id));
    }
  };

  const editPackage = (pkg) => {
    setFormData({
      address: pkg.address,
      weight: pkg.weight.toString(),
      date: pkg.date
    });
    setCapturedImage(pkg.image);
    setEditingId(pkg.id);
    setView('add');
  };

  const getPackagesByDate = (date) => {
    return packages.filter(p => p.date === date);
  };

  const getPackagesCount = () => {
    const counts = {};
    packages.forEach(p => {
      counts[p.date] = (counts[p.date] || 0) + 1;
    });
    return counts;
  };

  const getMonthlyStats = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const stats = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      stats.push({ day, count: getPackagesByDate(date).length });
    }
    return stats;
  };

  const getYearlyStats = (year) => {
    const stats = [];
    const months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    for (let month = 0; month < 12; month++) {
      const monthPackages = packages.filter(p => {
        const pDate = new Date(p.date);
        return pDate.getFullYear() === year && pDate.getMonth() === month;
      });
      stats.push({ month: months[month], count: monthPackages.length });
    }
    return stats;
  };

  const today = new Date().toISOString().split('T')[0];
  const todayCount = getPackagesByDate(today).length;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthCount = packages.filter(p => {
    const pDate = new Date(p.date);
    return pDate.getMonth() === currentMonth && pDate.getFullYear() === currentYear;
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-red-50">
      <div className="max-w-4xl mx-auto p-4">
        {/* Header */}
        <div className="bg-yellow-400 rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Package className="w-8 h-8" />
            DHL Paket-Tracker
          </h1>
        </div>

        {/* Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setView('dashboard')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              view === 'dashboard' ? 'bg-yellow-400 text-gray-800' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Calendar className="w-5 h-5" />
            Übersicht
          </button>
          <button
            onClick={() => { resetForm(); setView('add'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              view === 'add' ? 'bg-yellow-400 text-gray-800' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Plus className="w-5 h-5" />
            Paket erfassen
          </button>
          <button
            onClick={() => setView('stats')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              view === 'stats' ? 'bg-yellow-400 text-gray-800' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            Auswertungen
          </button>
        </div>

        {/* Dashboard View */}
        {view === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-gray-600 text-sm">Heute</div>
                <div className="text-3xl font-bold text-yellow-600">{todayCount}</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4">
                <div className="text-gray-600 text-sm">Dieser Monat</div>
                <div className="text-3xl font-bold text-yellow-600">{monthCount}</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Kalender</h2>
              <div className="grid grid-cols-7 gap-2">
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600 p-2">
                    {day}
                  </div>
                ))}
                {(() => {
                  const firstDay = new Date(currentYear, currentMonth, 1).getDay();
                  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                  const offset = firstDay === 0 ? 6 : firstDay - 1;
                  const counts = getPackagesCount();
                  const days = [];
                  
                  for (let i = 0; i < offset; i++) {
                    days.push(<div key={`empty-${i}`} />);
                  }
                  
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const count = counts[date] || 0;
                    days.push(
                      <button
                        key={day}
                        onClick={() => {
                          setSelectedDate(date);
                          setView('details');
                        }}
                        className={`p-2 rounded-lg text-center transition ${
                          count > 0 
                            ? 'bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        } ${date === today ? 'ring-2 ring-red-500' : ''}`}
                      >
                        <div className="text-sm">{day}</div>
                        {count > 0 && <div className="text-xs">{count}</div>}
                      </button>
                    );
                  }
                  return days;
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Add Package View */}
        {view === 'add' && (
          <div className="bg-white rounded-lg shadow p-6 space-y-6">
            <h2 className="text-xl font-bold">{editingId ? 'Paket bearbeiten' : 'Neues Paket'}</h2>
            
			{!capturedImage && (
			  <div className="space-y-3">
				<button
				  onClick={() => fileInputRef.current?.click()}
				  className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition"
				>
				  <Camera className="w-5 h-5" />
				  Foto aufnehmen / auswählen
				</button>

				<input
				  ref={fileInputRef}
				  type="file"
				  accept="image/*"
				  capture="environment"   // <- auf dem Handy öffnet das direkt die Kamera
				  onChange={handleFileUpload}
				  className="hidden"
				/>
			  </div>
			)}

            {showCamera && (
              <div className="relative">
                <video
					ref={videoRef}
					autoPlay
					playsInline
					muted     // ← MUSS SEIN
					className="w-full rounded-lg bg-black"
				/>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={capturePhoto}
                    className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold py-3 rounded-lg transition"
                  >
                    Aufnehmen
                  </button>
                  <button
                    onClick={stopCamera}
                    className="px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />

            {capturedImage && (
              <div className="relative">
                <img src={capturedImage} alt="Paket" className="w-full rounded-lg" />
                <button
                  onClick={() => setCapturedImage(null)}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Empfängeradresse
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  rows="3"
                  placeholder="Name, Straße, PLZ Ort, Land"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gewicht (kg)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Datum
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>
            </div>

            <button
              onClick={savePackage}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-800 font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition"
            >
              <Check className="w-5 h-5" />
              {editingId ? 'Speichern' : 'Paket hinzufügen'}
            </button>
          </div>
        )}

        {/* Details View */}
        {view === 'details' && selectedDate && (
          <div className="space-y-4">
            <button
              onClick={() => setView('dashboard')}
              className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
            >
              ← Zurück zur Übersicht
            </button>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('de-DE', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </h2>
              
              <div className="space-y-4">
                {getPackagesByDate(selectedDate).map(pkg => (
                  <div key={pkg.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex gap-4">
                      {pkg.image && (
                        <img src={pkg.image} alt="Paket" className="w-24 h-24 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 mb-1">{pkg.address}</div>
                        <div className="text-sm text-gray-600">Gewicht: {pkg.weight} kg</div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => editPackage(pkg)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => deletePackage(pkg.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {getPackagesByDate(selectedDate).length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    Keine Pakete an diesem Tag
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Stats View */}
        {view === 'stats' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Monatsübersicht {currentYear}</h2>
              <div className="space-y-2">
                {getMonthlyStats(currentYear, currentMonth).map(stat => (
                  <div key={stat.day} className="flex items-center gap-3">
                    <div className="w-8 text-sm text-gray-600">{stat.day}.</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                      {stat.count > 0 && (
                        <div
                          className="bg-yellow-400 h-full flex items-center justify-end pr-2 text-sm font-bold text-gray-800 transition-all"
                          style={{ width: `${Math.max((stat.count / Math.max(...getMonthlyStats(currentYear, currentMonth).map(s => s.count))) * 100, 10)}%` }}
                        >
                          {stat.count}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Jahresübersicht {currentYear}</h2>
              <div className="space-y-2">
                {getYearlyStats(currentYear).map(stat => (
                  <div key={stat.month} className="flex items-center gap-3">
                    <div className="w-12 text-sm text-gray-600">{stat.month}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                      {stat.count > 0 && (
                        <div
                          className="bg-yellow-400 h-full flex items-center justify-end pr-2 text-sm font-bold text-gray-800 transition-all"
                          style={{ width: `${Math.max((stat.count / Math.max(...getYearlyStats(currentYear).map(s => s.count))) * 100, 10)}%` }}
                        >
                          {stat.count}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Gesamtstatistik</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-600 text-sm">Gesamt Pakete</div>
                  <div className="text-2xl font-bold text-yellow-600">{packages.length}</div>
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Gesamtgewicht</div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {packages.reduce((sum, p) => sum + p.weight, 0).toFixed(2)} kg
                  </div>
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Ø pro Tag</div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {packages.length > 0 
                      ? (packages.length / new Set(packages.map(p => p.date)).size).toFixed(1)
                      : 0}
                  </div>
                </div>
                <div>
                  <div className="text-gray-600 text-sm">Ø Gewicht</div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {packages.length > 0 
                      ? (packages.reduce((sum, p) => sum + p.weight, 0) / packages.length).toFixed(2)
                      : 0} kg
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DHLPackageTracker;